import { Button, DEFAULT_PRIMARY_THEME, theme, useAuth } from '@idu-nightlife/ui-kit';
import { Logout, Security } from '@mui/icons-material';
import { AppBar, Box, Stack, Toolbar, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback } from 'react';
import { useManagerSelection } from '../../../app/contexts/manager-selection';
import IDUInlineLogo from '../../assets/idu-inline.svg?react';
import IDULogo from '../../assets/idu-with-subtitle.svg?react';
import { ApplicationRoleSelector } from './application-role-selector';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: DEFAULT_PRIMARY_THEME.darkest,
  boxShadow: theme.shadows[2],
  borderRadius: 0,
}));

function useLogoutWithChildMessaging() {
  const { logOut } = useAuth();

  return useCallback(() => {
    // logOut() already broadcasts { type: 'sign-out' } to all child iframes.
    void logOut();
  }, [logOut]);
}

export const GlobalNav = () => {
  const { firebaseUser } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isAuthenticated = !!firebaseUser;
  const { availableApps, selectedItem, selectItem, selectedAppConfig } = useManagerSelection();

  const handleLogoutClick = useLogoutWithChildMessaging();

  if (!isAuthenticated) return null;

  return (
    <StyledAppBar position="static">
      <Toolbar>
        <Stack
          direction="row"
          sx={{ width: isMobile ? '65px' : '196px', pt: 2, pb: 1.8, color: 'primary.contrastText' }}
        >
          {isMobile ? <IDUInlineLogo /> : <IDULogo />}
        </Stack>

        <Box
          // Remount on app change so the clipped shape repaints cleanly. A
          // clip-path parallelogram whose background-color changes in place
          // leaves a 1px sliver of the previous colour in its anti-aliased
          // edge texels (visible at DPR 2 with a fractional toolbar height).
          key={selectedAppConfig.id}
          sx={{
            width: isMobile ? 45 : 60,
            alignSelf: 'stretch',
            backgroundColor: selectedAppConfig.color,
            clipPath: 'polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)',
          }}
        >
          &nbsp;
        </Box>

        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', ml: 1, gap: 2, flex: 1 }}
        >
          <ApplicationRoleSelector
            applicationRoles={availableApps}
            onSelect={selectItem}
            selected={selectedItem}
          />

          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Button
              to="/account/security"
              color="inherit"
              aria-label="Account Security"
              startIcon={isMobile ? undefined : <Security />}
              sx={{ minWidth: 44, color: 'primary.contrastText' }}
            >
              {isMobile ? <Security /> : 'Account Security'}
            </Button>
            <Box
              data-testid="global-nav-logout"
              sx={{
                gap: '4px',
                fontWeight: 500,
                display: 'flex',
                cursor: 'pointer',
                justifyContent: 'center',
                color: 'primary.contrastText',
              }}
              onClick={handleLogoutClick}
            >
              <Logout />
              {!isMobile && 'Logout'}
            </Box>
          </Stack>
        </Stack>
      </Toolbar>
    </StyledAppBar>
  );
};

export default GlobalNav;
